import '@/styles/silk-confirm-modal.css';
import { type ReactNode } from 'react';
import { useLocale } from '@/context/LocaleContext';

export interface PurchaseConfirmModalProps {
  className?: string;
  open: boolean;
  headerTitle: string;
  price: number;
  balance: number | null;
  balanceLoading?: boolean;
  confirming?: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}

export function PurchaseConfirmModal({
  className = '',
  open,
  headerTitle,
  price,
  balance,
  balanceLoading = false,
  confirming = false,
  confirmLabel,
  onConfirm,
  onClose,
  children,
}: PurchaseConfirmModalProps) {
  const { t } = useLocale();

if (!open) return null;

  const canAfford = balance !== null && balance >= price;
  const canConfirm = !balanceLoading && canAfford && !confirming;

  return (
    <div
      className={`reset-spin-modal silk-confirm-modal ${className}`}
      role="dialog"
      aria-modal


    >
      <div className="reset-spin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="reset-spin-modal-header">
          <h3>{headerTitle}</h3>
          <button type="button" className="battlepass-exp-modal-close tm-modal-close" aria-label={t('silk_confirm_close')} disabled={confirming} onClick={onClose}>×</button>
        </div>

        <div className="reset-spin-modal-body">
          {children}

          <div className="silk-confirm-details">
            <p className="reset-spin-silk-amount">
              <strong>{t('silk_confirm_cost', { amount: price.toLocaleString() })}</strong>
            </p>
            <p className="silk-confirm-balance">
              {balanceLoading ? (
                <span
                  className="silk-confirm-skeleton-line silk-confirm-skeleton-line--wide"
                  aria-label={t('silk_confirm_checking')}
                />
              ) : balance !== null ? (
                t('silk_confirm_balance', { amount: balance.toLocaleString() })
              ) : (
                <span
                  className="silk-confirm-skeleton-line silk-confirm-skeleton-line--wide"
                  aria-hidden
                />
              )}
            </p>
            <p
              className={`silk-confirm-after${
                !balanceLoading && balance !== null && !canAfford ? ' silk-confirm-after--low' : ''
              }`}
            >
              {balanceLoading || balance === null ? (
                <span
                  className="silk-confirm-skeleton-line silk-confirm-skeleton-line--medium"
                  aria-hidden
                />
              ) : (
                t('silk_confirm_after', {
                  amount: Math.max(0, balance - price).toLocaleString(),
                })
              )}
            </p>
            <p
              className={`silk-confirm-warning silk-confirm-warning--slot${
                !canAfford && !balanceLoading && balance !== null
                  ? ' silk-confirm-warning--visible'
                  : ''
              }`}
            >
              {!canAfford && !balanceLoading && balance !== null
                ? t('silk_confirm_not_enough')
                : '\u00A0'}
            </p>
          </div>
        </div>

        <div className="reset-spin-modal-footer">
          <button type="button" className="reset-spin-btn-cancel" onClick={onClose} disabled={confirming}>
            {t('silk_confirm_cancel')}
          </button>
          <button
            type="button"
            className="reset-spin-btn-confirm"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {confirming
              ? t('tm_processing')
              : !canAfford && !balanceLoading && balance !== null
                ? t('silk_confirm_not_enough_short')
                : (confirmLabel ?? t('tm_buy_confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
}
